import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AlarmsDrawer from '../drawers/AlarmsDrawer';

/**
 * Normalizes alarm numeric fields coming from legacy JSON or form state.
 */
const normalizeInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

export default function AlarmManager({alarms, model_analyzers, disabled, availableModels, initialHornId = -1, initialAllTags = null}) {
    const [zones, setZones] = useState([]);
    const [trafficLights] = useState(() => JSON.parse(document.getElementById('react-zones')?.dataset.trafficLights || '[]'));
    const [tags, setTags] = useState([]);
    const [allTags, setAllTags] = useState(() => Array.isArray(initialAllTags) ? initialAllTags : []);
    const [alarmsData, setAlarms] = useState([]);
    const [caption, setCaption] = useState(false);
    const [selectedHornId, setSelectedHornId] = useState(initialHornId);
    const tagsFetchInFlight = useRef(false);

    const isNotificationsEnabled = useCallback((value) => {
        return value !== false;
    }, []);

    const updateZonesAndTags = useCallback(() => {
            // ZONE INPUTS
            const advanced = document.querySelectorAll('input.zones_advanced');
            const ids = document.querySelectorAll('input.zones_id');
            const armed = document.querySelectorAll('input.zones_armed');
            const points = document.querySelectorAll('input.zones_points');
            const metas = document.querySelectorAll('input.zones_meta');
            // get the selecte option data caption from #input-model var captioning = $(this).find('option:selected').data('captioning');
            const captioningInput = document.getElementById('input-model');
            let captioning = false;
            if (captioningInput) {
                const selectedOption = captioningInput.options[captioningInput.selectedIndex];
                console.log("Selected option for captioning", selectedOption);
                captioning = selectedOption ? selectedOption.getAttribute('data-captioning') == '1' : false;
            }
            setCaption(captioning);
            // parse the zones data

            const zonesData = Array.from(advanced).map((input, index) => {
                try {
                    const data = JSON.parse(input.value);
                // check if we have analyzers in the data and pass the hint and hint_file_url from model_analyzers
                    if (data.analyzers && data.analyzers.length > 0) {
                        data.analyzers = data.analyzers.map((analyzer) => {
                            let found = model_analyzers.find((model_analyzer) => model_analyzer.name === analyzer.model);
                            if (found) {
                                analyzer.hint = found.hint;
                                analyzer.hint_file_url = found.hint_file_url;
                            }
                            return analyzer;
                        });
                    }

                    return {
                        ...data,
                        traffic_light_ids: JSON.parse(metas[index]?.value || '{}').traffic_light_ids || [],
                        id: ids[index]?.value ?? null,
                        armed: JSON.parse(armed[index]?.value || '[]'),
                        points: JSON.parse(points[index]?.value || '[]')
                    };
                } catch (error) {
                    console.error('Error parsing zone JSON:', error);
                    return null;
                }
            }).filter(zone => zone !== null);

            setZones(zonesData);

            // TAGS INPUTS
            const tagSelect = document.querySelector('select.input-tags');
            if (tagSelect) {
                const selectedTags = Array.from(tagSelect.selectedOptions).map(option => ({
                    value: option.value,
                    text: option.textContent
                }));
                setTags(selectedTags);
                console.log("Tags updated", selectedTags);
            }

            console.log("Zones and tags updated",zonesData);
    }, [model_analyzers]);

    // Ensures AlarmManager does not depend solely on legacy events for tag metadata.
    const bootstrapAllTags = useCallback(async () => {
        if (initialAllTags !== null || allTags.length || tagsFetchInFlight.current) {
            return;
        }

        if (typeof window !== 'undefined' && Array.isArray(window.__lastAllTagsPayload) && window.__lastAllTagsPayload.length) {
            setAllTags(window.__lastAllTagsPayload);
            return;
        }

        const zoneContainer = document.getElementById('react-zones');
        const tagsUrl = zoneContainer?.dataset?.tagsUrl;
        if (!tagsUrl) {
            return;
        }

        tagsFetchInFlight.current = true;
        try {
            const response = await fetch(`${tagsUrl}?model=`);
            if (!response.ok) {
                throw new Error(`Failed to bootstrap tags: HTTP ${response.status}`);
            }
            const payload = await response.json();
            const nextTags = Array.isArray(payload?.tags) ? payload.tags : [];
            if (nextTags.length) {
                if (typeof window !== 'undefined') {
                    window.__lastAllTagsPayload = nextTags;
                }
                setAllTags(nextTags);
            }
        } catch (error) {
            console.error('AlarmManager failed to fetch tags for drawer bootstrap', error);
        } finally {
            tagsFetchInFlight.current = false;
        }
    }, [allTags.length, initialAllTags]);

    useEffect(() => {
        // Initial load
        updateZonesAndTags();

        const handleAllTagsUpdated = (event) => {
            const data = event.detail; // this is your array
            if (typeof window !== 'undefined') {
                window.__lastAllTagsPayload = data;
            }
            setAllTags(Array.isArray(data) ? data : []);
            // console.log("Received all tags:", data);
        };

        if (initialAllTags === null && typeof window !== 'undefined' && Array.isArray(window.__lastAllTagsPayload) && window.__lastAllTagsPayload.length) {
            setAllTags(window.__lastAllTagsPayload);
        }

        window.addEventListener("allTags:updated", handleAllTagsUpdated);
        window.addEventListener("zones:updated", updateZonesAndTags);
        window.addEventListener("zones:metadata:update", updateZonesAndTags);
        window.addEventListener("zones:manager:loaded", updateZonesAndTags);

        bootstrapAllTags();

        return () => {

            window.removeEventListener("allTags:updated", handleAllTagsUpdated);
            window.removeEventListener("zones:updated", updateZonesAndTags);
            window.removeEventListener("zones:metadata:update", updateZonesAndTags);
            window.removeEventListener("zones:manager:loaded", updateZonesAndTags);
        };
    }, [updateZonesAndTags, bootstrapAllTags, initialAllTags]);

    useEffect(() => {
        if (!allTags.length) {
            bootstrapAllTags();
        }
    }, [allTags.length, bootstrapAllTags]);

    useEffect(() => {
        const handleHornSelectionChanged = (event) => {
            const nextValue = Number.parseInt(event?.detail?.hornId ?? '-1', 10);
            setSelectedHornId(Number.isFinite(nextValue) ? nextValue : -1);
        };

        window.addEventListener('item:hornSelectionChanged', handleHornSelectionChanged);

        return () => {
            window.removeEventListener('item:hornSelectionChanged', handleHornSelectionChanged);
        };
    }, []);

    const updateAlarms = useCallback((alarms) => {
        setAlarms(alarms);
         console.log("Alarms updated", alarms);
        // prepare data for save go over all the alarms to prepare the data

        let normalizedData = alarms.map(alarm => {
            let zone_id = alarm.zone_id;
            let objects =[];
            let missing_objects = [];
            let displayObjectsData = [];
            alarm.objects.forEach(obj => {
                let classes =[];
                let classes_id =[];
                let detectorTags = [];
                let detectorTag = '';
                let detectorValue ='';
                let detectorText ='';
                classes_id.push(obj.object.tag.id);
                classes.push(obj.object.tag.name);
                if (obj.detector) {
                    detectorTag = obj.detector.tag.id;
                    detectorTags= obj.detector.tags.map(tag => tag.id);
                    detectorValue = obj.detector.value;
                    detectorText = obj.detector.text;
                }
                if (obj.object.tags){
                    obj.object.tags.forEach(tag => {
                        classes_id.push(tag.id);
                        classes.push(tag.name);
                    });
                }

               if (obj.missing) {
                    missing_objects.push(classes);
               } else {
                    objects.push(classes);
               }
               displayObjectsData.push({
                   classes: classes,
                   classes_id: classes_id,
                   detectorTags: detectorTags,
                   detectorTag: detectorTag,
                   detectorValue: detectorValue,
                   detectorText: detectorText,
                   missing: obj.missing,
                   value: obj.object.value
               });
            });

            let analysis = [];
            let displayAnalysisData = [];
            if (alarm.analysis && alarm.analysis.length > 0) {
                analysis = alarm.analysis.filter(a => a.analysisData).reduce((prev, analysis) => {
                    let class_id = analysis.analysisData.class_id;
                    let className = analysis.analysisData.class_name;
                    let model = analysis.analysisData.model;
                    let name = analysis.analysisData.name;
                    let value= analysis.detail;
                    let limitCumulate = Boolean(analysis.limit_cumulate);

                    if (!prev[className]) {
                        prev[className] = [];
                    }
                    prev[className].push({
                        model: model,
                        name: name,
                        value: value,
                        limit_cumulate: limitCumulate,
                    });
                    displayAnalysisData.push({
                        model: model,
                        name: name,
                        value: value,
                        class_name: className,
                        class_id: class_id,
                        limit_cumulate: limitCumulate,
                    });
                    return prev;
                },{});
            }

            return {
                version: 1,
                rule_id: alarm.id,
                rule: alarm.rule,
                status: alarm.status,
                notifications_enabled: isNotificationsEnabled(alarm.notifications_enabled),
                threshold: alarm.threshold,
                min_duration: normalizeInteger(alarm.min_duration, 0),
                text_reasoning: alarm.text_reasoning || '',
                alarm_frequency: normalizeInteger(alarm.alarm_frequency, 0),
                zone_id: zone_id,
                objects: objects,
                missing_objects: missing_objects,
                displayObjectsData: displayObjectsData,
                analysis: analysis,
                displayAnalysisData: displayAnalysisData,
                traffic_light_actions: alarm.traffic_light_actions || [],
                ...(selectedHornId >= 0 ? { audio_message: (alarm.audio_message || '').toString().trim().slice(0, 2000) } : {}),
            };
        });
        console.log("Normalized data", normalizedData);

        // save the alarms as json to the filed input.alarms
        const alarmsInput = document.querySelector('input.alarmsData');
        alarmsInput.value = JSON.stringify(normalizedData);

        const conversionStateInput = document.getElementById('simpleAlarmConversionState');
        if (conversionStateInput && conversionStateInput.value && conversionStateInput.value !== '{}' && conversionStateInput.value !== '[]') {
            conversionStateInput.value = '';

            const conversionActionInput = document.getElementById('simpleAlarmConversionAction');
            if (conversionActionInput) {
                conversionActionInput.value = '';
            }

            const clearConversionInput = document.getElementById('clearSimpleAlarmConversion');
            if (clearConversionInput) {
                clearConversionInput.value = '1';
            }

            window.dispatchEvent(new CustomEvent('item:simpleAlarmConversion:cleared'));
        }
    }, [isNotificationsEnabled, selectedHornId]);

    useEffect(() => {
        if (disabled || selectedHornId >= 0 || alarmsData.length === 0) {
            return;
        }

        const hasAudioMessages = alarmsData.some((alarm) => Boolean((alarm?.audio_message || '').trim()));
        if (!hasAudioMessages) {
            return;
        }

        const clearedAlarms = alarmsData.map((alarm) => ({
            ...alarm,
            audio_message: '',
        }));
        setAlarms(clearedAlarms);
        updateAlarms(clearedAlarms);
    }, [alarmsData, disabled, selectedHornId, updateAlarms]);

    function classIdToClass(class_id) {
        // go over alltags and find by id the class
        let found = allTags.find(tag => tag.id == class_id);
        if (found) {
            return found;
        } else {
            return {name: class_id};
        }
    }
    useEffect(() => {
        if (allTags && (allTags.length > 0 || disabled)) {
            let alarmsData = alarms.map(alarm => {
                // console.log("Alarm", alarm);
                // console.log('Zones', zones);
                let zonaData =zones.find((zone,idx) => (idx+1).toString() === alarm.zone_id.toString());
                // console.log("Zona data", zonaData);
                let zone_name = 'Entire zone';
                let hasMissingObjects = alarm.missing_objects && alarm.missing_objects.length > 0;
                if (alarm.zone_id.toString() !='0') {
                    zone_name = "Zona " +  alarm.zone_id;
                }
                let analysisInfoData = zonaData?.analyzers || [];
                // console.log("Analysis info data", analysisInfoData);
                let analysisData = alarm.displayAnalysisData.map(analysis => {
                    const limitCumulate = Boolean(analysis.limit_cumulate);

                    // find analysisInfoData where name model and class_name match
                    let analysisInfo = analysisInfoData.find(info => info.name == analysis.name && info.model == analysis.model && info.class == analysis.class_id);
                    if (!analysisInfo) {
                        return {
                            analysisData: null,
                            detail: null,
                            limit_cumulate: limitCumulate
                        };
                    }
                    const normalizedClassId = analysisInfo?.class_id ?? analysisInfo?.class ?? 0;
                    const tag = classIdToClass(normalizedClassId);
                    const enrichedAnalysisInfo = {
                        ...analysisInfo,
                        class_id: normalizedClassId,
                        class_name: tag.name,
                        tag: tag
                    };
                    // console.log("Analysis info", analysisInfo);
                    return {
                        analysisData: enrichedAnalysisInfo,
                        detail: analysis.value,
                        limit_cumulate: limitCumulate,
                    }
                }).filter(a => a.analysisData !== null);
                let objectData = alarm.displayObjectsData.map(obj => {
                    let tag = {};
                    let tags =[];
                    let classes =[];
                    let classes_id = obj.classes_id;
                    // debugger;
                    // check if classes_id is array
                    let classObjs =classIdToClass(obj.classes_id)
                    if (!Array.isArray(classes_id)) {
                        tag =classObjs;
                        classes.push(classes_id);
                    }else{
                        classes_id.forEach((class_id, index) => {
                            let classObjs = classIdToClass(class_id);
                            if (index == 0)
                                tag = classObjs;
                            if (index > 0) {
                                tags.push(classObjs);
                            }
                            classes.push(class_id);
                        });
                    }
                    let detectorTag = obj.detectorTag;
                    let detectorTags =obj.detectorTags || [];
                    let detectorValue =obj.detectorValue;
                    let detectorText =obj.detectorText;
                    let detector = null;
                    if ( detectorTag&& detectorTags.length > 0) {
                        detector = {
                            value: detectorValue,
                            text: detectorText,
                            tag: classIdToClass(detectorTag),
                            tags: detectorTags.map(classIdToClass)
                        };
                    }

                    return {
                        value: obj.value,
                        missing: obj.missing,
                        detector:detector,
                        object: {
                            value:  obj.value,
                            text: (obj.classes || []).join('-'),
                            tag: tag,
                            tags:tags,
                            classes: classes,
                        }
                    }
                });
                return {
                    version:1,
                    id: alarm.rule_id,
                    rule: alarm.rule,
                    hasMissingObjects: hasMissingObjects,
                    status: alarm.status || 'inactive',
                    notifications_enabled: isNotificationsEnabled(alarm.notifications_enabled),
                    min_duration: normalizeInteger(alarm.min_duration, 0),
                    text_reasoning: alarm.text_reasoning || '',
                    audio_message: alarm.audio_message || '',
                    traffic_light_actions: alarm.traffic_light_actions || [],
                    alarm_frequency: normalizeInteger(alarm.alarm_frequency, 0),
                    threshold: alarm.threshold,
                    zone_id: alarm.zone_id,
                    zone: zone_name,
                    objects: objectData,
                    analysis: analysisData
                }

            });
console.log("Alarms denormalized", alarmsData);
            setAlarms(alarmsData);
        }
    }, [alarms, allTags, zones, isNotificationsEnabled, disabled]);

    const memoizedAlarmsDrawer = useMemo(() => {
        return allTags && (allTags.length > 0 || disabled) && (
            <AlarmsDrawer trafficLights={trafficLights} alarms={alarmsData} zones={zones} tags={tags} onUpdate={updateAlarms} allTags={allTags} disabled={disabled} availableModels={availableModels} initialCaption={caption} selectedHornId={selectedHornId}/>
        );
    }, [alarmsData, zones, tags, allTags, updateAlarms, availableModels, caption, disabled, selectedHornId, trafficLights]);

    return memoizedAlarmsDrawer;
}
